import { useEffect, useRef } from 'react';
import Sortable, { type SortableEvent } from 'sortablejs';
import styles from './DragAndDropTable.module.css';

interface UseDragAndDropOptions<T> {
    items: T[];
    onReorder: (reorderedItems: T[]) => void;
    element: HTMLElement | null;
    animation: number;
    dragHandleSelector?: string;
}

export function useDragAndDrop<T>({ items, onReorder, element, animation, dragHandleSelector }: UseDragAndDropOptions<T>) {
    const currentItemsRef = useRef<T[]>(items);

    useEffect(() => {
        currentItemsRef.current = items;
    }, [items]);

    useEffect(() => {
        if (!element) {
            return;
        }

        const sortableInstance = Sortable.create(element, {
            animation,
            handle: dragHandleSelector,
            chosenClass: styles.chosenRow,
            ghostClass: styles.ghostRow,
            dragClass: styles.dragRow,
            onEnd: (evt: SortableEvent) => {
                const { oldIndex, newIndex } = evt;

                if (oldIndex == null || newIndex == null || oldIndex === newIndex) {
                    return;
                }

                const reorderedItems = [...currentItemsRef.current];
                const [movedItem] = reorderedItems.splice(oldIndex, 1);

                if (movedItem) {
                    reorderedItems.splice(newIndex, 0, movedItem);
                    onReorder(reorderedItems);
                }
            }
        });

        return () => {
            sortableInstance.destroy();
        };
    }, [animation, dragHandleSelector, onReorder, element]);
}
